import { useState, useEffect, useCallback } from 'react';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { SplashScreen } from 'src/components/loading-screen';

import { Role } from 'src/type';
import { useAuthContext } from '../hooks';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
  allowedRoles?: Role[];
};

export default function AuthGuard({ children, allowedRoles }: Props) {
  const { loading } = useAuthContext();

  return (
    <>
      {loading ? <SplashScreen /> : <Container allowedRoles={allowedRoles}>{children}</Container>}
    </>
  );
}

// ----------------------------------------------------------------------

function Container({ children, allowedRoles }: Props) {
  const router = useRouter();
  const { authenticated, user } = useAuthContext();
  const [checked, setChecked] = useState(false);

  const check = useCallback(() => {
    if (!authenticated) {
      const searchParams = new URLSearchParams({
        returnTo: window.location.pathname,
      }).toString();

      const href = `${paths.auth.login}?${searchParams}`;
      router.replace(href);
    } else {
      setChecked(true);
    }
  }, [authenticated, router]);

  const checkRole = useCallback(() => {
    if (!allowedRoles) {
      return true;
    }

    if (!user?.currentRole?.role) {
      router.replace('/no-access');
      return false;
    }

    const hasPermission = allowedRoles.includes(user.currentRole.role as Role);
    if (!hasPermission) {
      router.replace('/no-access');
      return false;
    }

    return true;
  }, [user, router, allowedRoles]);

  useEffect(() => {
    check();
  }, [check]);

  useEffect(() => {
    if (checked) {
      checkRole();
    }
  }, [checked, checkRole]);

  if (!checked) {
    return null;
  }

  return <>{children}</>;
}
